/**
 * AuthContext for AccountsGenie
 * Provides authentication state and methods throughout the application
 * Integrates with AA backend authentication system
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '../stores/authStore';
import type {
  LoginCredentials,
  RegisterCredentials,
  UpdateProfileData,
  User,
  AuthState
} from '../types/auth';

// AuthContext interface
export interface AuthContextType extends AuthState {
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  clearError: () => void;
  refreshToken: () => Promise<void>;
  
  // Computed properties
  isAdmin: boolean;
  isAccountant: boolean;
  isClient: boolean;
  userFullName: string;
  userInitials: string;
  
  // Multi-tenant support
  getTenantId: () => string | null;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    tenantId,
    login,
    register,
    logout,
    checkAuth,
    updateProfile,
    refreshAuthToken,
    clearError,
    getTenantId,
  } = useAuthStore();

  // Check authentication status on mount
  useEffect(() => {
    if (!isAuthenticated && !user) {
      checkAuth();
    }
  }, [checkAuth, isAuthenticated, user]);

  // Auto-refresh token when it's about to expire
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Set up token refresh interval (every 50 minutes for a 1-hour token)
    const refreshInterval = setInterval(() => {
      refreshAuthToken().catch((error) => {
        console.error('Auto token refresh failed:', error);
        // On failure, the store will automatically log out the user
      });
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, user, refreshAuthToken]);

  // Computed properties
  const isAdmin = user?.role === 'admin';
  const isAccountant = user?.role === 'accountant';
  const isClient = user?.role === 'client';
  
  const userFullName = user?.fullName || user?.name || 
    (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : '');
    
  const userInitials = user
    ? user.firstName && user.lastName 
      ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
      : user.name 
        ? user.name.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase()
        : user.email.charAt(0).toUpperCase()
    : '';

  const contextValue: AuthContextType = {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    clearError,
    refreshToken: refreshAuthToken,
    
    // Computed properties
    isAdmin,
    isAccountant,
    isClient,
    userFullName,
    userInitials,
    
    // Multi-tenant support
    getTenantId,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the AuthContext
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// Hook for checking if user has specific permissions
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuthContext();

  const hasRole = (role: User['role']) => {
    return isAuthenticated && user?.role === role;
  };

  const hasAnyRole = (roles: User['role'][]) => {
    return isAuthenticated && user?.role && roles.includes(user.role);
  };

  const canAccessAdminFeatures = () => {
    return hasRole('admin');
  };

  const canAccessAccountantFeatures = () => {
    return hasAnyRole(['admin', 'accountant']);
  };

  const canAccessClientFeatures = () => {
    return hasAnyRole(['admin', 'accountant', 'client']);
  };

  const canManageUsers = () => {
    return hasRole('admin');
  };

  const canViewFinancialData = () => {
    return hasAnyRole(['admin', 'accountant']);
  };

  const canEditOwnProfile = () => {
    return isAuthenticated;
  };

  return {
    hasRole,
    hasAnyRole,
    canAccessAdminFeatures,
    canAccessAccountantFeatures,
    canAccessClientFeatures,
    canManageUsers,
    canViewFinancialData,
    canEditOwnProfile,
  };
};

// Hook for authentication guards in components
export const useAuthGuard = () => {
  const { isAuthenticated, isLoading, user } = useAuthContext();

  const requireAuth = () => {
    if (isLoading) return { allowed: false, reason: 'loading' };
    if (!isAuthenticated) return { allowed: false, reason: 'unauthenticated' };
    return { allowed: true, reason: null };
  };

  const requireRole = (role: User['role']) => {
    const authCheck = requireAuth();
    if (!authCheck.allowed) return authCheck;
    
    if (user?.role !== role) {
      return { allowed: false, reason: 'insufficient_permissions' };
    }
    
    return { allowed: true, reason: null };
  };

  const requireAnyRole = (roles: User['role'][]) => {
    const authCheck = requireAuth();
    if (!authCheck.allowed) return authCheck;
    
    if (!user?.role || !roles.includes(user.role)) {
      return { allowed: false, reason: 'insufficient_permissions' };
    }
    
    return { allowed: true, reason: null };
  };

  return {
    requireAuth,
    requireRole,
    requireAnyRole,
  };
};

export default AuthContext;